import { ClockCounterClockwise, ArrowCounterClockwise, X } from '@phosphor-icons/react';
import { Banner, Button, Text, Flex } from '@chatsdet/ui';
import { formatUpdatedAt } from './utils';
import type { VersionSelection } from './VersionDropdown';
import { formatDraftVersionLabel, formatPublishedVersionLabel } from './versionHistory';

type HistoryBannerProps = {
  version: VersionSelection;
  publishedAt: string | null;
  versionName?: string | null;
  onRestore: () => void;
  onBackToCurrent: () => void;
};

export function HistoryBanner({
  version,
  publishedAt,
  versionName,
  onRestore,
  onBackToCurrent,
}: HistoryBannerProps) {
  const versionLabel =
    version.type === 'snapshot'
      ? formatPublishedVersionLabel(version.version)
      : version.type === 'revision'
        ? `Revision ${version.revision}`
        : version.type === 'draft-snapshot'
          ? formatDraftVersionLabel(version.basePublishedVersion, version.version)
          : 'Current definition';
  const restoreLabel =
    version.type === 'revision'
      ? 'Restore revision'
      : version.type === 'draft-snapshot'
        ? 'Restore checkpoint'
        : 'Restore legacy version';

  return (
    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2">
      {version.type === 'draft-snapshot' && (
        <Banner variant="info" className="shadow-lg max-w-lg text-center">
          <Text size="xs" className="text-muted-foreground">
            Restoring a checkpoint only applies the fields it captured — anything it didn&apos;t
            capture keeps its current value.
          </Text>
        </Banner>
      )}
      <Banner variant="warning" className="shadow-lg whitespace-nowrap">
        <Flex align="center" gap="sm">
          <ClockCounterClockwise
            size={18}
            className="text-amber-600 dark:text-amber-400 shrink-0"
          />
          <Text size="sm" weight="medium">
            Viewing {versionLabel}
            {versionName && <span className="ml-1">&mdash; {versionName}</span>}
            {publishedAt && (
              <span className="text-amber-600 dark:text-amber-400 ml-1">
                &bull; {formatUpdatedAt(publishedAt)}
              </span>
            )}
          </Text>
        </Flex>
        <Flex align="center" gap="xs">
          <Button
            variant="secondary"
            size="sm"
            iconLeft={<ArrowCounterClockwise size={14} />}
            onPress={onRestore}
          >
            {restoreLabel}
          </Button>
          <Button variant="ghost" size="sm" iconLeft={<X size={14} />} onPress={onBackToCurrent}>
            Back to Current
          </Button>
        </Flex>
      </Banner>
    </div>
  );
}
